/**
 * Phase 84.27 v3.6.0 — Goal celebration modal.
 *
 * Fires when a goal's progress crosses 100% for the first time. Backend
 * marks `celebration_pending: true` in the goal's progress payload; we
 * show this modal, then POST /api/goals/{id}/celebrate to stamp
 * celebrated_at so it never fires again for the same completion.
 *
 * For debt_payoff goals the modal has an explicit "confirm this loan is
 * paid off" step — auto-completion of a loan is a bigger claim than an
 * investment target and we don't want to celebrate a data glitch.
 *
 * Copy varies by goal.kind so the moment feels earned, not templated.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Sparkles, ShieldCheck, HandCoins, Compass, Mountain, Home, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Kind-specific headline + copy. Falls back to a generic feel-good line.
const CELEBRATIONS = {
  emergency: {
    icon: ShieldCheck,
    headline: 'Your safety net is complete.',
    copy:     'You built the buffer that keeps future-you steady. Small emergencies stay small now.',
  },
  debt: {
    icon: HandCoins,
    headline: "You're free.",
    copy:     "That debt is no longer part of your monthly math. This is a rare and quiet kind of freedom.",
  },
  fi: {
    icon: Compass,
    headline: 'You crossed a marker on the road to freedom.',
    copy:     'Your invested capital just hit the number you set. Keep walking — the direction is right.',
  },
  freedom: {
    icon: Compass,
    headline: 'A meaningful mile.',
    copy:     'You said "this much" and you got there. Sit with that for a moment before deciding what comes next.',
  },
  home: {
    icon: Home,
    headline: 'The down payment is here.',
    copy:     'You saved for something specific and it exists now. Real bricks, real keys.',
  },
  education: {
    icon: GraduationCap,
    headline: 'The tuition is ready.',
    copy:     'You built the runway. Whoever is learning next has a smoother start because of what you did.',
  },
  travel: {
    icon: Mountain,
    headline: 'The trip is funded.',
    copy:     "Pack. Go. You earned this one — literally.",
  },
  sabbatical: {
    icon: Mountain,
    headline: 'The pause is paid for.',
    copy:     'You bought yourself time. That is one of the rarer things money can buy.',
  },
  business: {
    icon: Sparkles,
    headline: 'Your seed is planted.',
    copy:     'The idea has capital behind it now. What you do next is up to you.',
  },
  custom: {
    icon: CheckCircle2,
    headline: 'You did it.',
    copy:     'You named a number and you reached it. Not everyone finishes what they start.',
  },
};


export default function GoalCelebrationModal({ goal, onClose }) {
  const [confirming, setConfirming] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(
    (goal?.progress?.tracking_mode === 'debt_payoff'),
  );

  useEffect(() => {
    // Reset confirm state whenever a new goal appears.
    setNeedsConfirm(goal?.progress?.tracking_mode === 'debt_payoff');
  }, [goal?.id]);

  if (!goal) return null;

  const kind = (goal.kind || 'custom').toLowerCase();
  const spec = CELEBRATIONS[kind] || CELEBRATIONS.custom;
  const Icon = spec.icon;

  const dismiss = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(
        `${BACKEND_URL}/api/goals/${goal.id}/celebrate`,
        {},
        { headers: { Authorization: `Bearer ${session?.access_token || ''}` } },
      );
    } catch (e) {
      // Non-blocking — the modal already closed on the user's screen.
      console.warn('celebrate stamp failed', e);
    }
    onClose?.();
  };

  const confirmPayoff = async () => {
    setConfirming(true);
    try {
      await dismiss();
      toast.success('Loan marked as paid off. Congratulations 🎉', { duration: 5000 });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={!!goal} onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="max-w-md rounded-2xl overflow-hidden text-center" data-testid="goal-celebration-modal">
        {/* Screen-reader-only title + description to satisfy Radix a11y
            without changing the visual design (headline is the styled h2 below). */}
        <VisuallyHidden asChild>
          <DialogTitle>{`Goal achieved: ${goal.title}`}</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>{spec.headline} {spec.copy}</DialogDescription>
        </VisuallyHidden>
        {/* Subtle confetti — CSS-only, respects prefers-reduced-motion. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="absolute block h-1.5 w-1.5 rounded-full"
              style={{
                left:  `${(i * 8.3) % 100}%`,
                top:   '-6px',
                background: ['#FFD57A','#F5A78F','#B9DDA5','#B0D0F0','#D9C4FF'][i % 5],
                animation: 'goal-confetti 1400ms cubic-bezier(.2,.7,.4,1) forwards',
                animationDelay: `${(i % 6) * 60}ms`,
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes goal-confetti {
            0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
            100% { transform: translateY(420px) rotate(540deg); opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes goal-confetti { 0%,100% { opacity: 0; } }
          }
        `}</style>

        <div className="relative z-10 py-6 space-y-4">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10">
            <Icon className="h-8 w-8 text-primary" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--gold))] font-medium mb-1">
              {goal.title}
            </div>
            <h2 className="font-display text-2xl leading-tight">{spec.headline}</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            {spec.copy}
          </p>

          {needsConfirm ? (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded-xl p-3 mt-2">
              We noticed the linked loan balance hit zero. Confirm it&rsquo;s truly paid off before we lock this in.
            </div>
          ) : null}
        </div>

        <DialogFooter className="relative z-10 flex-col-reverse sm:flex-row sm:justify-center gap-2 pb-4">
          {needsConfirm ? (
            <>
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="goal-celebration-not-yet"
              >
                Not yet
              </Button>
              <Button
                onClick={confirmPayoff}
                disabled={confirming}
                data-testid="goal-celebration-confirm-payoff"
              >
                Yes — it&rsquo;s paid off
              </Button>
            </>
          ) : (
            <Button onClick={dismiss} data-testid="goal-celebration-savor">
              Just savor this
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
